import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Plan } from "@brain/models";
import { TransactionResponse } from "@brain/pages/checkout/data/transaction";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, take } from "rxjs/operators";
import { Transaction } from "../data/transaction";
import { BASE_URI } from "./token";

@Injectable({
    providedIn: "root"
})
export class TransactionService {

    public constructor(
        private httpClient: HttpClient,
        @Inject(BASE_URI) private apiUrl: string
    ) { }

    public resolveOfferRequests(role: "buyer" | "asset_owner" = "buyer"): Observable<Transaction[]> {
        return this.httpClient
            .get(this.apiUrl + "/services/subscriptions", { params: { product_id: "DCSP0030", role } })
            .pipe(
                switchMap((invitatioResults: any[]) => {
                    /** map all invitatio subscriptions to transactions */
                    const innerReq = invitatioResults.map<Observable<Transaction>>(
                        (invitatio) => {
                            return this.httpClient
                                .get(this.apiUrl + "/terms/transactions/" + invitatio.subscription_id)
                                .pipe(
                                    /** merge plans into the result the id is not enough */
                                    mergeMap((res: TransactionResponse) => {
                                        const { product_id, sku_id, plan_id } = res.transaction;
                                        return this.httpClient.get<Plan>(this.apiUrl
                                            + "/catalog/global/products/" + product_id
                                            + "/skus/" + sku_id
                                            + "/plans/" + plan_id
                                        ).pipe(
                                            /** add plan to transaction, and return the transaction */
                                            map((plan: Plan) => {
                                                res.transaction.plan = plan;
                                                return res.transaction;
                                            }),
                                        );
                                    }),
                                    catchError(() => of(void 0)),
                                    take(1)
                                );
                        }
                    );

                    return forkJoin(innerReq);
                }),
                map((res) => res.filter((transaction) => transaction !== undefined))
            );
    }

    /**
     * request a new offer
     */
    public requestNewOffer(transaction_id: string): any {
        const uri = `${this.apiUrl}/terms/transactions/${transaction_id}/invitatio/request_offer`;
        return this.httpClient.post<any>(uri, { transaction_id: transaction_id });
    }

    /**
     * get transaction by id
     */
    public getTransaction(id: string): Observable<Transaction> {
        const uri = `${this.apiUrl}/terms/transactions/${id}`;
        return this.httpClient.get<{ transaction: Transaction }>(uri)
            .pipe(
                switchMap((res) => {
                    const { transaction } = res;
                    const { product_id, sku_id, plan_id } = transaction;
                    const plan$ = this.httpClient.get<Plan>(this.apiUrl + `/catalog/global/products/${product_id}/skus/${sku_id}/plans/${plan_id}`);

                    return plan$.pipe(
                        map((plan) => {
                            transaction.plan = plan;
                            return transaction;
                        })
                    );
                })
            );
    }

    public updateTransactionPlan(transaction_id: string, plan: Plan): any {
        const uri = `${this.apiUrl}/terms/transactions/${transaction_id}/plan`;
        const update = { ...plan, public: false, plan_id: transaction_id };
        return this.httpClient.patch<Plan>(uri, { plan: update })
            .pipe(map(() => update));
    }

    public updateContract(transaction_id: string, contract: string): Observable<void> {
        const uri = `${this.apiUrl}/terms/transactions/${transaction_id}/terms/tos/update`;
        return this.httpClient.patch<void>(uri, { text: contract });
    }

    public proposeContract(transaction_id: string, contract: string): Observable<void> {
        const uri = `${this.apiUrl}/terms/transactions/${transaction_id}/invitatio/propose`;
        return this.httpClient.patch<void>(uri, { text: contract });
    }

    /**
     * decline a transaction
     */
    public declineTransaction(transaction_id: string): any {
        const uri = `${this.apiUrl}/terms/transactions/${transaction_id}/invitatio/decline`;
        return this.httpClient.patch<void>(uri, { transaction_id });
    }
}
